import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { performanceService } from '../services/performanceOptimization';

// Safe imports with fallbacks
let routePreloader: any, userDataCache: any, imagePreloader: any, behaviorAnalytics: any, connectionAwareLoading: any;

try {
  routePreloader = require('../services/routePreloader').routePreloader;
  userDataCache = require('../services/userDataCache').userDataCache;
  imagePreloader = require('../services/imagePreloader').imagePreloader;
  behaviorAnalytics = require('../services/behaviorAnalytics').behaviorAnalytics;
  connectionAwareLoading = require('../services/connectionAwareLoading').connectionAwareLoading;
} catch (error) {
  console.warn('Some preloading services failed to load:', error);
}

interface PerformanceMetrics {
  appLoadTime: number;
  routeChangeTime: number;
  apiResponseTimes: Record<string, number[]>;
  componentRenderTimes: Record<string, number[]>;
  memoryUsage?: MemoryInfo;
  cacheHitRate: number;
  errorRate: number;
}

interface PerformanceContextType {
  metrics: PerformanceMetrics;
  isMonitoring: boolean;

  // Performance tracking
  trackRouteChange: (route: string) => void;
  trackApiCall: (endpoint: string, duration: number) => void;
  trackComponentRender: (component: string, duration: number) => void;
  trackError: (error: Error, context?: string) => void;

  // Performance optimization
  optimizeImages: boolean;
  enableCaching: boolean;
  lazyLoadImages: boolean;

  // Settings
  toggleMonitoring: () => void;
  updateOptimizationSettings: (settings: Partial<{
    optimizeImages: boolean;
    enableCaching: boolean;
    lazyLoadImages: boolean;
  }>) => void;

  // Reports
  getPerformanceReport: () => any;
  clearMetrics: () => void;

  // New preloading features
  preloading: {
    preloadRoute: (route: string, priority?: 'critical' | 'high' | 'medium' | 'low') => Promise<void>;
    preloadUserData: (userIds: string[], priority?: 'critical' | 'high' | 'medium' | 'low') => void;
    preloadImages: (urls: string[], priority?: 'critical' | 'high' | 'medium' | 'low') => Promise<string[]>;
    preloadUserImages: (users: Array<{ id: string; photoURL?: string; photos?: string[] }>, priority?: 'critical' | 'high' | 'medium' | 'low') => Promise<void>;
    isRoutePreloaded: (route: string) => boolean;
    isImagePreloaded: (url: string) => boolean;
    getOptimalImageUrl: (url: string, size?: string) => string;
  };

  // Analytics and insights
  analytics: {
    trackUserAction: (type: string, data: any, route?: string) => void;
    getPredictedActions: (currentRoute: string, limit?: number) => any[];
    getUserSegment: () => any;
    getPreloadingStrategy: () => any;
  };

  // Connection-aware settings
  connection: {
    getCurrentStrategy: () => any;
    shouldPreload: (type?: 'routes' | 'images' | 'data') => boolean;
    getImageConfig: () => any;
    getConnectionQuality: () => number;
    isDataConscious: () => boolean;
  };
}

const PerformanceContext = createContext<PerformanceContextType | undefined>(undefined);

export const usePerformance = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within PerformanceProvider');
  }
  return context;
};

interface PerformanceProviderProps {
  children: ReactNode;
}

export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({ children }) => {
  const [isMonitoring, setIsMonitoring] = useState(true);
  const [optimizeImages, setOptimizeImages] = useState(true);
  const [enableCaching, setEnableCaching] = useState(true);
  const [lazyLoadImages, setLazyLoadImages] = useState(true);

  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    appLoadTime: 0,
    routeChangeTime: 0,
    apiResponseTimes: {},
    componentRenderTimes: {},
    cacheHitRate: 0,
    errorRate: 0
  });

  const [routeChangeStart, setRouteChangeStart] = useState<number>(0);
  const [errorCount, setErrorCount] = useState(0);
  const [totalRequests, setTotalRequests] = useState(0);
  const [currentRoute, setCurrentRoute] = useState('');

  // Initialize performance monitoring
  useEffect(() => {
    if (!isMonitoring) return;

    // Track app load time
    const loadTime = performance.now();
    setMetrics(prev => ({ ...prev, appLoadTime: loadTime }));

    // Set up performance observers
    if ('PerformanceObserver' in window) {
      // Navigation timing
      const navObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            console.log('Navigation timing:', {
              domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
              loadComplete: navEntry.loadEventEnd - navEntry.loadEventStart
            });
          }
        }
      });
      
      try {
        navObserver.observe({ entryTypes: ['navigation'] });
      } catch (error) {
        console.warn('Performance observer not supported:', error);
      }

      // Resource timing
      const resourceObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'resource') {
            const resourceEntry = entry as PerformanceResourceTiming;
            if (resourceEntry.name.includes('/api/')) {
              const duration = resourceEntry.responseEnd - resourceEntry.requestStart;
              trackApiCall(resourceEntry.name, duration);
            }
          }
        }
      });
      
      try {
        resourceObserver.observe({ entryTypes: ['resource'] });
      } catch (error) {
        console.warn('Resource observer not supported:', error);
      }

      return () => {
        navObserver.disconnect();
        resourceObserver.disconnect();
      };
    }
  }, [isMonitoring]);

  // Update memory usage periodically
  useEffect(() => {
    if (!isMonitoring) return;

    const interval = setInterval(() => {
      const memoryInfo = performanceService.measureMemoryUsage();
      if (memoryInfo) {
        setMetrics(prev => ({ ...prev, memoryUsage: memoryInfo }));
      }
    }, 10000); // Every 10 seconds

    return () => clearInterval(interval);
  }, [isMonitoring]);

  const trackRouteChange = (route: string) => {
    if (!isMonitoring) return;

    const now = performance.now();
    const prevRoute = currentRoute;

    if (routeChangeStart > 0) {
      const duration = now - routeChangeStart;
      setMetrics(prev => ({ ...prev, routeChangeTime: duration }));
    }

    setRouteChangeStart(now);
    setCurrentRoute(route);

    // Track with analytics and route preloader (with safety checks)
    if (prevRoute && prevRoute !== route) {
      try {
        behaviorAnalytics?.trackRouteChange?.(prevRoute, route);
        routePreloader?.recordNavigation?.(prevRoute, route);
        behaviorAnalytics?.trackAction?.('route_change', { from: prevRoute, to: route }, route);
      } catch (error) {
        // Silently handle tracking errors
      }
    }
  };

  const trackApiCall = (endpoint: string, duration: number) => {
    if (!isMonitoring) return;

    setTotalRequests(prev => prev + 1);
    
    setMetrics(prev => ({
      ...prev,
      apiResponseTimes: {
        ...prev.apiResponseTimes,
        [endpoint]: [...(prev.apiResponseTimes[endpoint] || []), duration].slice(-10) // Keep last 10
      }
    }));
  };

  const trackComponentRender = (component: string, duration: number) => {
    if (!isMonitoring) return;

    setMetrics(prev => ({
      ...prev,
      componentRenderTimes: {
        ...prev.componentRenderTimes,
        [component]: [...(prev.componentRenderTimes[component] || []), duration].slice(-20) // Keep last 20
      }
    }));
  };

  const trackError = (error: Error, context?: string) => {
    if (!isMonitoring) return;

    setErrorCount(prev => prev + 1);
    
    const errorRate = totalRequests > 0 ? (errorCount + 1) / totalRequests : 0;
    setMetrics(prev => ({ ...prev, errorRate }));

    // Log error to analytics
    analyticsService.logError(error.message, {
      context,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });

    // Log error for debugging
    console.error('Performance tracked error:', {
      message: error.message,
      stack: error.stack,
      context,
      timestamp: new Date().toISOString()
    });
  };

  const toggleMonitoring = () => {
    setIsMonitoring(prev => !prev);
  };

  const updateOptimizationSettings = (settings: Partial<{
    optimizeImages: boolean;
    enableCaching: boolean;
    lazyLoadImages: boolean;
  }>) => {
    if (settings.optimizeImages !== undefined) setOptimizeImages(settings.optimizeImages);
    if (settings.enableCaching !== undefined) setEnableCaching(settings.enableCaching);
    if (settings.lazyLoadImages !== undefined) setLazyLoadImages(settings.lazyLoadImages);
  };

  const getPerformanceReport = () => {
    const serviceReport = performanceService.getPerformanceReport();
    
    // Calculate averages
    const avgApiResponseTime = Object.values(metrics.apiResponseTimes)
      .flat()
      .reduce((sum, time, _, arr) => sum + time / arr.length, 0);

    const avgComponentRenderTime = Object.values(metrics.componentRenderTimes)
      .flat()
      .reduce((sum, time, _, arr) => sum + time / arr.length, 0);

    return {
      ...serviceReport,
      ...metrics,
      averageApiResponseTime: avgApiResponseTime,
      averageComponentRenderTime: avgComponentRenderTime,
      optimizationSettings: {
        optimizeImages,
        enableCaching,
        lazyLoadImages
      },
      recommendations: generateRecommendations()
    };
  };

  const generateRecommendations = () => {
    const recommendations = [];
    
    // Check component render times
    const slowComponents = Object.entries(metrics.componentRenderTimes)
      .filter(([_, times]) => {
        const avg = times.reduce((sum, time) => sum + time, 0) / times.length;
        return avg > 16; // 16ms is 60fps threshold
      })
      .map(([component]) => component);

    if (slowComponents.length > 0) {
      recommendations.push({
        type: 'performance',
        priority: 'high',
        message: `Slow components detected: ${slowComponents.join(', ')}. Consider optimizing render logic.`
      });
    }

    // Check API response times
    const slowApis = Object.entries(metrics.apiResponseTimes)
      .filter(([_, times]) => {
        const avg = times.reduce((sum, time) => sum + time, 0) / times.length;
        return avg > 1000; // 1 second threshold
      })
      .map(([endpoint]) => endpoint);

    if (slowApis.length > 0) {
      recommendations.push({
        type: 'api',
        priority: 'medium',
        message: `Slow API endpoints detected: ${slowApis.join(', ')}. Consider caching or optimization.`
      });
    }

    // Check memory usage
    if (metrics.memoryUsage && metrics.memoryUsage.usedJSHeapSize > 50 * 1024 * 1024) { // 50MB
      recommendations.push({
        type: 'memory',
        priority: 'medium',
        message: 'High memory usage detected. Consider cleaning up unused resources.'
      });
    }

    // Check error rate
    if (metrics.errorRate > 0.05) { // 5% error rate
      recommendations.push({
        type: 'errors',
        priority: 'high',
        message: `High error rate detected (${(metrics.errorRate * 100).toFixed(1)}%). Review error logs.`
      });
    }

    return recommendations;
  };

  const clearMetrics = () => {
    setMetrics({
      appLoadTime: 0,
      routeChangeTime: 0,
      apiResponseTimes: {},
      componentRenderTimes: {},
      cacheHitRate: 0,
      errorRate: 0
    });
    setErrorCount(0);
    setTotalRequests(0);
    performanceService.cleanupUnusedResources();
    try {
      userDataCache?.clearAllCaches?.();
      imagePreloader?.clearCache?.();
    } catch (error) {
      // Silently handle cleanup errors
    }
  };

  // New preloading methods with safety checks
  const preloading = {
    preloadRoute: async (route: string, priority: 'critical' | 'high' | 'medium' | 'low' = 'medium') => {
      try {
        if (!connectionAwareLoading?.shouldPreload?.('routes')) return;
        await routePreloader?.forcePreload?.([route]);
      } catch (error) {
        // Silently handle preload errors
      }
    },

    preloadUserData: (userIds: string[], priority: 'critical' | 'high' | 'medium' | 'low' = 'medium') => {
      try {
        if (!connectionAwareLoading?.shouldPreload?.('data')) return;
        userDataCache?.schedulePrefetch?.(userIds, priority);
      } catch (error) {
        // Silently handle preload errors
      }
    },

    preloadImages: async (urls: string[], priority: 'critical' | 'high' | 'medium' | 'low' = 'medium') => {
      try {
        if (!connectionAwareLoading?.shouldPreload?.('images')) return [];
        const requests = urls.map(url => ({ url, priority }));
        return await imagePreloader?.preloadImages?.(requests) || [];
      } catch (error) {
        return [];
      }
    },

    preloadUserImages: async (users: Array<{ id: string; photoURL?: string; photos?: string[] }>, priority: 'critical' | 'high' | 'medium' | 'low' = 'medium') => {
      try {
        if (!connectionAwareLoading?.shouldPreload?.('images')) return;
        await imagePreloader?.preloadUserImages?.(users, priority);
      } catch (error) {
        // Silently handle preload errors
      }
    },

    isRoutePreloaded: (route: string) => {
      try {
        return routePreloader?.getPreloadedComponent?.(route) !== null;
      } catch (error) {
        return false;
      }
    },

    isImagePreloaded: (url: string) => {
      try {
        return imagePreloader?.isImageCached?.(url) || false;
      } catch (error) {
        return false;
      }
    },

    getOptimalImageUrl: (url: string, size = 'medium') => {
      try {
        return imagePreloader?.getOptimizedUrl?.(url, size) || url;
      } catch (error) {
        return url;
      }
    }
  };

  // Analytics methods with safety checks
  const analytics = {
    trackUserAction: (type: string, data: any, route?: string) => {
      try {
        behaviorAnalytics?.trackAction?.(type as any, data, route);
      } catch (error) {
        // Silently handle analytics errors
      }
    },

    getPredictedActions: (currentRoute: string, limit = 5) => {
      try {
        return behaviorAnalytics?.getPredictedNextActions?.(currentRoute, limit) || [];
      } catch (error) {
        return [];
      }
    },

    getUserSegment: () => {
      try {
        return behaviorAnalytics?.getUserSegment?.() || null;
      } catch (error) {
        return null;
      }
    },

    getPreloadingStrategy: () => {
      try {
        return behaviorAnalytics?.getPreloadingStrategy?.() || { priority: 'balanced', prefetchDepth: 3 };
      } catch (error) {
        return { priority: 'balanced', prefetchDepth: 3 };
      }
    }
  };

  // Connection-aware methods with safety checks
  const connection = {
    getCurrentStrategy: () => {
      try {
        return connectionAwareLoading?.getCurrentStrategy?.() || { name: 'balanced' };
      } catch (error) {
        return { name: 'balanced' };
      }
    },

    shouldPreload: (type: 'routes' | 'images' | 'data' = 'images') => {
      try {
        return connectionAwareLoading?.shouldPreload?.(type) ?? false;
      } catch (error) {
        return false;
      }
    },

    getImageConfig: () => {
      try {
        return connectionAwareLoading?.getImageLoadingConfig?.() || { quality: 80, batchSize: 10 };
      } catch (error) {
        return { quality: 80, batchSize: 10 };
      }
    },

    getConnectionQuality: () => {
      try {
        return connectionAwareLoading?.getConnectionQuality?.() || 50;
      } catch (error) {
        return 50;
      }
    },

    isDataConscious: () => {
      try {
        return connectionAwareLoading?.isDataConscious?.() || false;
      } catch (error) {
        return false;
      }
    }
  };

  const value = {
    metrics,
    isMonitoring,
    trackRouteChange,
    trackApiCall,
    trackComponentRender,
    trackError,
    optimizeImages,
    enableCaching,
    lazyLoadImages,
    toggleMonitoring,
    updateOptimizationSettings,
    getPerformanceReport,
    clearMetrics,
    preloading,
    analytics,
    connection
  };

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
};

export default PerformanceContext;